'use client';

import {
  ChangeEvent,
  DetailedHTMLProps,
  HTMLAttributes,
  InputHTMLAttributes,
  KeyboardEvent,
  useRef,
} from 'react';
import { VariantProps } from 'tailwind-variants';
import { IsVisible } from '../../../utils';
import { pinStyles } from './styles';

const keyboardKeys = ['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Tab'];

export interface IPinFieldProps
  extends Omit<
      DetailedHTMLProps<
        InputHTMLAttributes<HTMLInputElement>,
        HTMLInputElement
      >,
      'type' | 'value' | 'onChange'
    >,
    VariantProps<typeof pinStyles.code> {
  error?: string;
  label?: string;
  labelProps?: {
    className?: string;
    rest?: HTMLAttributes<HTMLLabelElement>;
    variants?: VariantProps<typeof pinStyles.label>;
  };
  length?: number;
  onChange?: (value: string) => void;
  onComplete?: () => void;
  value?: string;
}

export function PinField({
  design,
  disabled = false,
  error,
  label,
  length = 4,
  name,
  onChange,
  onComplete,
  readOnly = false,
  value = '',
  ...rest
}: IPinFieldProps) {
  const inputRef = useRef<HTMLInputElement>(null);

  const codeKeys = Array.from({ length }).map((_, index) => `key-${index}`);

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (keyboardKeys.includes(event.key)) {
      event.preventDefault();
    }
  };

  const handleOnChange = (event: ChangeEvent<HTMLInputElement>) => {
    const newInputValue = event.target.value.replace(/\s/g, '');

    if (onChange) {
      onChange(newInputValue);
    }

    if (onComplete && newInputValue.length === length) {
      onComplete();
    }
  };

  return (
    <div className={pinStyles.container({})}>
      <IsVisible when={!!label}>
        <label
          className={pinStyles.label({
            errored: !!error,
          })}
          htmlFor={name}
        >
          {label}
        </label>
      </IsVisible>
      <div
        aria-hidden="true"
        className={pinStyles.content()}
        onClick={() => inputRef.current?.focus()}
      >
        <input
          autoComplete="off"
          className={pinStyles.input({
            disabled: disabled || readOnly,
          })}
          id={name}
          name={name}
          onChange={handleOnChange}
          onKeyDown={handleKeyDown}
          onSelect={event =>
            event.currentTarget.setSelectionRange(
              event.currentTarget.value.length,
              event.currentTarget.value.length,
            )
          }
          pattern="[0-9]*"
          ref={inputRef}
          type="tel"
          {...rest}
        />

        {codeKeys.map((codeKey, index) => (
          <div
            aria-hidden="true"
            className={pinStyles.code({
              design,
              filled: value.length > index,
              inactive: value.length < index,
              errored: !!error,
              selected:
                value.length === index ||
                (value.length === index + 1 && length === index + 1),
            })}
            key={codeKey}
            onClick={() => inputRef.current?.focus()}
          >
            {value[index]}
          </div>
        ))}
      </div>
      <IsVisible when={!!error}>
        <div className="flex mt-0 mx-auto w-fit text-right">
          <span className="text-primary-two text-xs text-right">{error}</span>
        </div>
      </IsVisible>
    </div>
  );
}
